import { Paper, Dialog as UnstyledDialog } from "@mui/material";
import styled from '@mui/styled-engine';
import moment from "moment";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useToggle } from "react-use";
import videojs from "video.js";
import Config from "../../../config";
import { Loader } from "../../components/Loader/Loader";
import { ZoomTransition } from "../../components/Transition/Transition";
import { getSelectedLanguage } from "../../hooks/useLanguage";
import useTitle from "../../hooks/useTitle";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { authTokenSelector } from "../Auth/authSlice";
import { familyIsActiveSelector, selectedFamilySelector } from "../Families/familySlice";
import { fetchPatients, patientsIsLoadingSelector, patientsSelector, selectedPatientsSelector, setSelectedPatient, setSelectedPatientToNextPatient } from "../Patients/patientsSlice";
import { MessageType, VideoMessage } from "./Components/VideoMessage";
import { VideoPlayer } from "./Components/VideoPlayer";
import "./skin.css";

const Dialog = styled(UnstyledDialog)(({ theme }: any) => ({
  padding: theme.spacing(0, 0, 0),
  display: "inline-block",
  height: "100%",
}));

const SimplePaper = styled(Paper)(() => ({
  boxShadow: 'none',
  padding: '0',
}));

export const Video = () => {
  const { t } = useTranslation();
  useTitle(t("BabyWatch") + " - " + t("video"));
  const dispatch = useAppDispatch();
  const selectedLanguage = getSelectedLanguage();
  const selectedPatient = useAppSelector(selectedPatientsSelector);
  const isLoading = useAppSelector(patientsIsLoadingSelector);
  const token = useAppSelector(authTokenSelector);
  const patients = useAppSelector(patientsSelector);
  const selectedFamily = useAppSelector(selectedFamilySelector);
  const [showDialog, setShowDialog] = useToggle(selectedPatient !== undefined);
  const isFamilyActive = useAppSelector(familyIsActiveSelector);

  const showNextButton = patients !== undefined && patients.length > 1 ? () => dispatch(setSelectedPatientToNextPatient()) : undefined
  const exitFullScreen = () => dispatch(setSelectedPatient(undefined))

  const videoJsOptions: videojs.PlayerOptions = {
    autoplay: true,
    controls: false,
    loop: true,
    muted: true,
    preload: 'auto',
    controlBar: {
      fullscreenToggle: true,
    },
  };

  useEffect(() => {
    moment.locale(selectedLanguage);
  }, [selectedLanguage]);

  useEffect(() => {
    if (selectedFamily?.community_id) {
      dispatch(fetchPatients(selectedFamily.community_id));
    }
  }, [dispatch, selectedFamily]);

  useEffect(() => {
    setShowDialog(selectedPatient !== undefined);
    if (selectedPatient === undefined && showDialog) {
      document.documentElement.style.overflow = 'unset';
    }
  }, [selectedPatient, setShowDialog, showDialog]);

  return (
    <Dialog fullScreen={true} open={showDialog} aria-labelledby="form-dialog-title" id="modal-add"
      TransitionComponent={ZoomTransition}
      PaperComponent={SimplePaper}>
      <Loader isLoading={isLoading} />
      {selectedPatient && (selectedPatient.stream_active || selectedPatient.wowza_stream_file?.startsWith("http") ? 
        <VideoPlayer videoJsOptions={
          {
            ...videoJsOptions, sources:
              [{
                src: selectedPatient.wowza_stream_file.startsWith("http") ? selectedPatient.wowza_stream_file : Config.mediaUrl + "/" + selectedPatient.wowza_stream_file + Config.mediaSuffix + "/playlist.m3u8?t=" + token?.replaceAll('.', '___'),
                type: "application/x-mpegURL"
              }]
          }} patientNumber={selectedPatient.patient_number}
          title={selectedPatient.first_name}
          nextAction={showNextButton}
          exitFullScreenAction={exitFullScreen}
          isActive={selectedPatient.wowza_stream_file.startsWith("http") ? true : selectedPatient.stream_active}
        />
        : VideoMessageElement(isFamilyActive, selectedPatient, showNextButton, exitFullScreen))
      }
    </Dialog>
  );
};

const VideoMessageElement = (isFamilyActive : any, selectedPatient : any, showNextButton : any, exitFullScreen : any) => {
  return <VideoMessage
    nextAction={showNextButton}
    exitFullScreenAction={exitFullScreen}
    messageType={selectedPatient.state === 'admitted' && isFamilyActive ? MessageType.Busy : MessageType.Discharged} />
}